import type { CallHistoryItem, MissedCallItem } from '@/types/call';
import type { CaseHistoryEntry } from '@/types/case';
import { formatCallerDisplayName } from '@/utils/customer';
import type { DisplayCall } from './CallHistoryCardTypes';

export function getOutcomeColor(outcome: string | null): {
  bgcolor: string;
  color: string;
} {
  if (!outcome) return { bgcolor: '#f3f4f6', color: '#6b7280' };
  const lowerOutcome = outcome.toLowerCase();
  if (lowerOutcome === 'resolved') {
    return { bgcolor: '#dcfce7', color: '#166534' };
  }
  if (lowerOutcome === 'escalated') {
    return { bgcolor: '#fee2e2', color: '#991b1b' };
  }
  if (lowerOutcome === 'pending') {
    return { bgcolor: '#fef9c3', color: '#854d0e' };
  }
  return { bgcolor: '#e0e7ff', color: '#3730a3' };
}

export function getCaseStatusColor(caseStatus: string | null): {
  bgcolor: string;
  color: string;
} {
  if (!caseStatus) return { bgcolor: '#f3f4f6', color: '#6b7280' };
  const lowerStatus = caseStatus.toLowerCase();
  if (lowerStatus === 'open' || lowerStatus === 'new') {
    return { bgcolor: '#dcfce7', color: '#166534' };
  }
  if (lowerStatus === 'closed') {
    return { bgcolor: '#e0e7ff', color: '#3730a3' };
  }
  return { bgcolor: '#fef9c3', color: '#854d0e' };
}

export function callHistoryItemToDisplayCall(
  call: CallHistoryItem
): DisplayCall {
  const startDate = call.startDate ?? call.createdAt;
  return {
    id: call.id,
    contactId: call.contactId,
    type: call.type ?? 'call',
    dateTime: new Date(startDate).toLocaleString(),
    phoneNumber: call.phoneNumber ?? call.clientDetails?.phoneNumber ?? '',
    clientDetails: call.clientDetails,
    caseId: call.caseId,
    pickedBy: call.pickedBy ?? [],
    startDate,
    endDate: call.endDate,
    reason: call.reason ?? null,
    outcome: call.outcome ?? null,
    caseStatus: call.caseStatus,
    hasRecording: call.hasRecording ?? false,
    workspaceId: call.workspaceId,
    isCallback: call.isCallback,
  };
}

export function missedCallItemToDisplayCall(call: MissedCallItem): DisplayCall {
  return {
    id: call.contactId,
    contactId: call.contactId,
    type: 'call',
    dateTime: new Date(call.createdAt).toLocaleString(),
    phoneNumber: call.clientDetails?.phoneNumber ?? '',
    clientDetails: {
      name: call.clientDetails?.name ?? '',
      phoneNumber: call.clientDetails?.phoneNumber ?? '',
      callType: 'call',
    },
    caseId: null,
    pickedBy: [],
    startDate: call.createdAt,
    endDate: null,
    reason: null,
    outcome: null,
    caseStatus: null,
    hasRecording: false,
    workspaceId: undefined,
  };
}

/** Missed / never-picked historical calls (no end time, no agents). */
export function isUnansweredHistoricalCall(call: DisplayCall): boolean {
  return !call.endDate && call.pickedBy.length === 0;
}

export function getCallDisplayName(
  call: DisplayCall,
  unknownCallerLabel?: string
): string {
  return formatCallerDisplayName(call.clientDetails?.name, unknownCallerLabel);
}

export function getMostRecentCaseHistoryEntry(
  caseHistory?: CaseHistoryEntry[]
): CaseHistoryEntry | undefined {
  if (!caseHistory?.length) return undefined;
  return caseHistory[caseHistory.length - 1];
}

export function hasWorkspaceCaseMismatch(
  workspaceId: string | number | null | undefined,
  caseHistory?: CaseHistoryEntry[]
): boolean {
  const ws =
    workspaceId != null && workspaceId !== '' ? Number(workspaceId) : null;
  if (ws == null || Number.isNaN(ws)) return false;
  const latest = getMostRecentCaseHistoryEntry(caseHistory);
  if (!latest) return false;
  return latest.workspaceId !== ws;
}

export function shouldOfferCreateCase(options: {
  enableCustomerLink: boolean;
  workspaceId: string | number | null | undefined;
  caseId: string | null | undefined;
  caseHistory?: CaseHistoryEntry[];
}): boolean {
  const { enableCustomerLink, workspaceId, caseId, caseHistory } = options;
  if (enableCustomerLink) {
    const ws =
      workspaceId != null && workspaceId !== '' ? Number(workspaceId) : null;
    if (ws == null || Number.isNaN(ws)) return false;
    if (!caseId) return true;
    return hasWorkspaceCaseMismatch(workspaceId, caseHistory);
  }
  return !caseId;
}

export function shouldOfferLinkCase(options: {
  caseId: string | null | undefined;
  workspaceId: string | number | null | undefined;
  caseHistory?: CaseHistoryEntry[];
}): boolean {
  if (!options.caseId) return false;
  return !hasWorkspaceCaseMismatch(options.workspaceId, options.caseHistory);
}
